import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCollection } from '@/hooks/firestore';
import { useAuth } from '@/stores/auth';
import { Card, EmptyState, LoadingSkeleton } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { CategoryPicker } from '@/components/ui/CategoryPicker';
import { toast } from '@/components/ui/Toast';
import { SellerSubNav } from '@/components/layout/SellerSubNav';
import { callUpdateSellerCategories } from '@/lib/callables';
import {
  CATEGORY_LABELS,
  formatZAR,
  sameCampusRegion,
  type RequestDoc,
  type ServiceCategory,
} from '@makhe/shared';

export default function SellerRequestsPage() {
  const { userDoc } = useAuth();
  const cats = (userDoc?.sellerProfile?.categories ?? []) as ServiceCategory[];
  const campus = userDoc?.location?.campus;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ServiceCategory[]>([]);
  const [saving, setSaving] = useState(false);

  const q = useMemo(() => {
    if (cats.length === 0) return null;
    return query(
      collection(db, 'requests'),
      where('status', '==', 'open'),
      where('category', 'in', cats.slice(0, 10)),
      orderBy('createdAt', 'desc'),
      limit(50),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cats.join(',')]);
  const { data: requests, loading } = useCollection<RequestDoc>(q);

  // Show requests from the same institution (campus labels are sub-campus
  // specific, so match on institution, not exact string), or with no campus set.
  // Exclude the viewer's own requests (a seller can't offer on their own).
  const visible = requests.filter(
    (r) => r.buyerId !== userDoc?.uid && sameCampusRegion(r.campus, campus),
  );

  const showPicker = editing || cats.length === 0;

  const openEdit = () => {
    setDraft(cats);
    setEditing(true);
  };

  const save = async () => {
    if (draft.length === 0) {
      toast.error('Pick at least one category.');
      return;
    }
    setSaving(true);
    try {
      await callUpdateSellerCategories({ categories: draft });
      toast.success('Categories saved.');
      setEditing(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SellerSubNav />
      <section className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="font-display text-3xl">Requests</h1>
        <p className="mt-2 text-sm text-makhe-muted">
          Open requests from students in your categories{campus ? ` on ${campus}` : ''}. Send an offer to win the job.
        </p>

        <div className="mt-6 rounded-xl border border-makhe-line p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium">Your categories</p>
              <p className="mt-0.5 text-xs text-makhe-muted">
                Requests are matched to you by these. You’re also added automatically for any
                category you actively list.
              </p>
            </div>
            {cats.length > 0 && !editing && (
              <button
                type="button"
                onClick={openEdit}
                className="shrink-0 text-xs text-makhe-primary underline"
              >
                Edit
              </button>
            )}
          </div>

          {showPicker ? (
            <div className="mt-3 space-y-3">
              <CategoryPicker value={draft} onChange={setDraft} />
              <div className="flex gap-2">
                <Button size="sm" loading={saving} onClick={() => void save()}>
                  Save categories
                </Button>
                {cats.length > 0 && (
                  <Button size="sm" variant="ghost" disabled={saving} onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              {cats.map((c) => (
                <span
                  key={c}
                  className="rounded-full bg-makhe-line/50 px-3 py-1 text-xs text-makhe-ink/80"
                >
                  {CATEGORY_LABELS[c]}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 space-y-3">
          {cats.length > 0 && loading && <LoadingSkeleton className="h-24" />}
          {cats.length > 0 && !loading && visible.length === 0 && (
            <EmptyState title="No open requests right now" description="You'll get notified the moment a student posts one in your category." />
          )}
          {visible.map((r) => (
            <Link key={r.id} to={`/seller/requests/${r.id}`}>
              <Card className="flex items-center gap-4">
                {r.referenceImageURLs?.[0] ? (
                  <img src={r.referenceImageURLs[0]} alt="" className="h-16 w-16 rounded-lg object-cover" />
                ) : (
                  <div className="grid h-16 w-16 place-items-center rounded-lg bg-makhe-line/50 text-xs text-makhe-muted/60">
                    {CATEGORY_LABELS[r.category].charAt(0)}
                  </div>
                )}
                <div className="flex-1">
                  <p className="font-medium">{r.title}</p>
                  <p className="text-xs text-makhe-muted">
                    {CATEGORY_LABELS[r.category]} · {r.buyerSnapshot.displayName}
                    {r.campus ? ` · ${r.campus}` : ''}
                  </p>
                  <p className="text-xs text-makhe-muted/80">
                    {r.budgetHintZARcents != null ? `Budget ~ ${formatZAR(r.budgetHintZARcents)}` : 'No budget set'} · {r.offerCount} offer{r.offerCount === 1 ? '' : 's'}
                  </p>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
